import React from "react";
import { Button } from "../Button/Button";
import FmeaStepProgress from "../FmeaStepProgress";
import FmeaStepHeader from "../FmeaStepHeader";
import styles from "./StepWrapper.module.css";

export interface StepWrapperProps {
  /**
   * Título principal do step
   */
  title: string;

  /**
   * Descrição ou subtítulo opcional do step
   */
  description?: string;

  /**
   * Número do step atual (para o progresso)
   */
  currentStep: number;

  /**
   * Conteúdo do step (inputs, cards, etc.)
   */
  children: React.ReactNode;

  /**
   * Classes CSS adicionais para o container principal
   */
  className?: string;

  /** Callback para voltar ao step anterior */
  onPrevious?: () => void;
  /** Label do botão anterior @default "Anterior" */
  previousLabel?: string;
  /** Callback para avançar ao próximo step */
  onNext: () => void;
  /** Label do botão próximo @default "Próximo" */
  nextLabel?: string;
  /** Desabilita o botão de próximo */
  nextDisabled?: boolean;
  /** Exibe estado de carregamento no botão próximo */
  nextLoading?: boolean;
  /** Callback para cancelar (usado no Step1) */
  onCancel?: () => void;
  /** Label do botão cancelar */
  cancelLabel?: string;
}

/**
 * StepWrapper - Componente de estrutura para as telas de Step do FMEA.
 * 
 * Padroniza o cabeçalho (progresso + título), o conteúdo e a navegação (anterior/próximo).
 */
export const StepWrapper: React.FC<StepWrapperProps> = ({
  title,
  description,
  currentStep,
  children,
  className,
  onPrevious,
  previousLabel,
  onNext,
  nextLabel,
  nextDisabled,
  nextLoading,
  onCancel,
  cancelLabel,
}) => {
  return (
    <div className={`${styles.container}${className ? ` ${className}` : ""}`}>
      {/* Progresso do FMEA */}
      <FmeaStepProgress currentStep={currentStep} />

      {/* Cabeçalho do Step */}
      <div className={styles.header}>
        <FmeaStepHeader title={title} description={description} />
      </div>

      {/* Conteúdo Principal */}
      <main className={styles.content}>
        {children}
      </main>

      {/* Navegação de Rodapé */}
      <div className={styles.footer}>
        <div className={styles.navigation}>
          {onCancel && (
            <Button
              label={cancelLabel || "Cancelar"}
              variant="outlined"
              severity="danger"
              icon="close"
              onClick={onCancel}
            />
          )}
          {onPrevious && (
            <Button
              label={previousLabel || "Anterior"}
              variant="outlined"
              severity="warning"
              icon="arrow_back"
              onClick={onPrevious}
            />
          )}
          <div className={styles.spacer} />
          <Button
            label={nextLabel || "Próximo"}
            severity="success"
            icon="arrow_forward"
            iconPos="right"
            onClick={onNext}
            disabled={nextDisabled}
            loading={nextLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default StepWrapper;
